import { Heading } from '@/components/catalyst-ui/heading'
import { Text } from '@/components/catalyst-ui/text'
import { Input } from '@/components/catalyst-ui/input'
import { Field, Label, Description } from '@/components/catalyst-ui/fieldset'
import { Select } from '@/components/catalyst-ui/select'
import { Checkbox, CheckboxField } from '@/components/catalyst-ui/checkbox'
import PhoneInput from '@/components/ui/PhoneInput'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { COUNTRIES } from '@/features/domains/constants/countries'

export default function ContactInfoStep({ formData, setFormData, errors, domain }) {
  const { user } = useAuth()
  const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }))

  return (
    <div className="space-y-2">
      <div className="text-center">
        <Heading level={3} className="!text-3xl !font-semibold text-gray-900">{domain} will soon be yours</Heading>
        <Text className="mt-2 !text-[#A1A1AA] !font-medium">Please confirm the contact information for your domain.</Text>
      </div>

      <div className="bg-[#F4F4F5] p-8 rounded-md sm:w-[610px]">
        <div className="grid grid-cols-1 gap-x-4 gap-y-2.5 sm:grid-cols-2">
          <Field className="[&>[data-slot=label]+[data-slot=control]]:!mt-1.5">
            <Label className="!text-sm !font-semibold">First name <span className="text-[#B91C1C]">*</span></Label>
            <Input
              value={formData.first_name}
              onChange={(e) => handleInputChange('first_name', e.target.value)}
              invalid={!!errors.first_name}
              placeholder="Jane Doe"
            />
            {errors.first_name && <p className="mt-1 !text-sm !text-[#B91C1C] !font-medium">{errors.first_name}</p>}
          </Field>

          <Field className="[&>[data-slot=label]+[data-slot=control]]:!mt-1.5">
            <Label className="!text-sm !font-semibold">Last name <span className="text-[#B91C1C]">*</span></Label>
            <Input
              value={formData.last_name}
              onChange={(e) => handleInputChange('last_name', e.target.value)}
              invalid={!!errors.last_name}
              placeholder="Jane Doe"
            />
            {errors.last_name && <p className="mt-1 !text-sm !text-[#B91C1C] !font-medium">{errors.last_name}</p>}
          </Field>

          <Field className="[&>[data-slot=label]+[data-slot=control]]:!mt-1.5">
            <Label className="!text-sm !font-semibold">Phone number <span className="text-[#B91C1C]">*</span></Label>
            <PhoneInput
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              invalid={!!errors.phone}
              error={errors.phone}
              placeholder="Enter phone number"
            />
          </Field>


          <Field className="[&>[data-slot=label]+[data-slot=control]]:!mt-1.5">
            <Label className="!text-sm !font-semibold">Company (Optional)</Label>
            <Input
              value={formData.org_name}
              onChange={(e) => handleInputChange('org_name', e.target.value)}
              maxLength={100}
              placeholder="Business Name"
            />
          </Field>

          <Field className="sm:col-span-2 [&>[data-slot=label]+[data-slot=control]]:!mt-1.5">
            <Label className="!text-sm !font-semibold">Email address <span className="text-[#B91C1C]">*</span></Label>
            <Input
              type="email"
              value={formData.email || user?.email || ''}
              disabled
              className="!bg-gray-100 !text-gray-500 cursor-not-allowed"
              placeholder="example@email.com"
            />
            {errors.email && <p className="mt-1 !text-sm !text-[#B91C1C] !font-medium">{errors.email}</p>}
          </Field>

          <div className="h-px sm:col-span-2 w-full bg-[#E4E4E7] my-3"/>

          <Field className="sm:col-span-2 [&>[data-slot=label]+[data-slot=control]]:!mt-1.5">
            <Label className="!text-sm !font-semibold">Street address <span className="text-[#B91C1C]">*</span></Label>
            <Input
              value={formData.address1}
              onChange={(e) => handleInputChange('address1', e.target.value)}
              invalid={!!errors.address1}
              maxLength={64}
              placeholder="e.g., 123 Main St"
            />
            {errors.address1 && <p className="mt-1 !text-sm !text-[#B91C1C] !font-medium">{errors.address1}</p>}
          </Field>

          <Field className="[&>[data-slot=label]+[data-slot=control]]:!mt-1.5">
            <Label className="!text-sm !font-semibold">City <span className="text-[#B91C1C]">*</span></Label>
            <Input
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              invalid={!!errors.city}
              maxLength={64}
              placeholder="e.g., New York"
            />
            {errors.city && <p className="mt-1 !text-sm !text-[#B91C1C] !font-medium">{errors.city}</p>}
          </Field>

          <Field className="[&>[data-slot=label]+[data-slot=control]]:!mt-1.5">
            <Label className="!text-sm !font-semibold">State <span className="text-[#B91C1C]">*</span></Label>
            <Input
              value={formData.state}
              onChange={(e) => handleInputChange('state', e.target.value)}
              invalid={!!errors.state}
              maxLength={64}
            />
            {errors.state && <p className="mt-1 !text-sm !text-[#B91C1C] !font-medium">{errors.state}</p>}
          </Field>

          <Field className="[&>[data-slot=label]+[data-slot=control]]:!mt-1.5">
            <Label className="!text-sm !font-semibold">Country <span className="text-[#B91C1C]">*</span></Label>
            <Select
              value={formData.country}
              onChange={(e) => handleInputChange('country', e.target.value)}
            >
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </Select>
          </Field>

          <Field className="[&>[data-slot=label]+[data-slot=control]]:!mt-1.5">
            <Label className="!text-sm !font-semibold">Postcode <span className="text-[#B91C1C]">*</span></Label>
            <Input
              value={formData.postal_code}
              onChange={(e) => handleInputChange('postal_code', e.target.value)}
              invalid={!!errors.postal_code}
              maxLength={16}
              placeholder={
                formData.country === 'US' ? '12345 or 12345-6789' :
                  formData.country === 'CA' ? 'A1A 1A1' :
                    formData.country === 'GB' ? 'SW1A 1AA' :
                      formData.country === 'AU' ? '2000' :
                        'Enter postal code'
              }
            />
            {errors.postal_code && <p className="mt-1 !text-sm !text-[#B91C1C] !font-medium">{errors.postal_code}</p>}
          </Field>
        </div>

        <div className="mt-6">
          <div className="mt-2">
            <CheckboxField>
              <Checkbox
                checked={formData.useSameContact}
                onChange={(value) => setFormData(prev => ({ ...prev, useSameContact: value }))}
              />
              <Label className="!text-sm !text-black !font-semibold">Tech Contact Info</Label>
              <Description className="!text-sm !font-medium">Use registrant contact info</Description>
            </CheckboxField>
          </div>
        </div>

        <div className="mt-6">
          <div className="mt-2">
            <CheckboxField>
              <Checkbox
                checked={formData.useSameContactAdmin}
                onChange={(value) => setFormData(prev => ({ ...prev, useSameContactAdmin: value }))}
              />
              <Label className="!text-sm !text-black !font-semibold">Admin Contact Info</Label>
              <Description className="!text-sm !font-medium">Use registrant contact info</Description>
            </CheckboxField>
          </div>
        </div>
      </div>
    </div>
  )
}
